import store from '@/store';
import { getDataFiles2D } from '@/functions/extractData.js';
import { DATA_TYPE_CONC, DATA_TYPE_AOF } from '@/common/sharedDefinitions/dataExtractors/baseExtractor.js';
import GeneratePlot from '@/functions/generatePlot.js';
import {
  PlotType,
  ConfigName,
  TypeAxis,
  StudyManager
} from '@/common/plotDefinitions.js';
import { ExtraConfigKey } from '@/common/sharedDefinitions/commonPlotDefinitions'
import { getReportFiles } from '@/common/commonFunctions.js';

const zoneNotify = 'ConfigWindowPrep';

const ConfWindowPrep = {
  //All function here must have the same parameters
  async CHRONO_VALUES(parent, typeCard) {
    const report = store.getters.getCurrentReportViewer;
    let response = {
      success: false,
      lastError: '',
      parent: parent,
    };
    const baseStudy = StudyManager.getStudyBase(typeCard);
    const xType = PlotType[baseStudy].xAxisType;
    const yType = PlotType[baseStudy].yAxisType;
    const dataFiles = await getDataFiles2D(
      parent,
      report,
      xType,
      yType,
      store.getters.getReportStatesListReportViewer
    );
    let concFile = null;
    const reportFiles = await getReportFiles(report)
    for (let j = 0; j < reportFiles.length; j++) {
      let file = reportFiles[j];
      if (file === null) {
        continue;
      }
      if (file.dataType === DATA_TYPE_CONC) {
        concFile = file;
        break;
      }
    }
    if (dataFiles.length === 0) {
      response.lastError =
        `The report needs files type Data to show the ${store.getters.getNotationReportViewer} Configuration`;
      return response;
    }
    // const xAxisTypeVal = TypeAxis.time.id;
    // const yAxisTypeVal = '';
    const plotlyRaw = await GeneratePlot.getPreprocPlot(
      parent,
      dataFiles,
      report.configuration[typeCard],
      report[ExtraConfigKey],
      true,
      xType,
      yType,
    );
    if (plotlyRaw === null || (!plotlyRaw.success && !plotlyRaw.correct)) {
      let errorMssg =
        plotlyRaw !== null && 'lastError' in plotlyRaw
          ? plotlyRaw.lastError
          : '';
      response.lastError = `Error retrieving the plot from the server. Contact the administrator of the system. ${errorMssg}`;
      return response;
    }

    let concentrations = [];
    if ('concTimes' in report.configuration[typeCard][ConfigName.BaseCurveFit]['multiȲ']) {
      concentrations = report.configuration[typeCard][ConfigName.BaseCurveFit]['multiȲ']['concTimes'];
    }
    if (concFile !== null && concentrations.length === 0) {
      concentrations = JSON.parse(JSON.stringify(concFile.extractedData));
      const currReportConf = report[ExtraConfigKey];
      const timeFactor = TypeAxis.time.getConvertFactor(currReportConf);
      for (let j = 0; j < concentrations.length; j++) {
        concentrations[j][0] *= timeFactor;
      }
      const succTitle = `${store.getters.getNotationReportViewer}s file found`
      const succText = `A ${store.getters.getNotationReportViewer} file (${concFile.name}) was found in your report. Added to the configuration`;
      parent.$notifTerm.addNotification(
        parent.$notifTerm.typesNotif.SUCCESS,
        succTitle,
        succText,
        zoneNotify,
      );
    }
    const plot =
      typeof plotlyRaw.dataGraph['']['PRE_PROCESS_GRAPH_BOOL'] !== 'undefined'
        ? plotlyRaw.dataGraph['']['PRE_PROCESS_GRAPH_BOOL']
        : plotlyRaw.dataGraph['']['RAW_GRAPH_BOOL'];
    parent.setPlotCPConf({
      data: plot.data,
      layout: plot.layout,
      concentrations: concentrations,
    });
    response.success = true;
    return response;
  },
  async AREA_INTEREST_VALUES(parent, typeCard) {
    const report = store.getters.getCurrentReportViewer;

    let response = {
      success: false,
      lastError: '',
      parent: parent,
    };
    const baseStudy = StudyManager.getStudyBase(typeCard);
    const xType = PlotType[baseStudy].xAxisType;
    const yType = PlotType[baseStudy].yAxisType;

    const dataFiles = await getDataFiles2D(
      parent,
      report,
      xType,
      yType,
      store.getters.getReportStatesListReportViewer
    );
    let concFile = null;
    const reportFiles = await getReportFiles(report)
    for (let j = 0; j < reportFiles.length; j++) {
      let file = reportFiles[j];
      if (file === null) {
        continue;
      }
      if (file.dataType === DATA_TYPE_AOF) {
        concFile = file;
        break;
      }
    }
    if (dataFiles.length === 0) {
      response.lastError =
        'The report needs files type Data to show the Area of Interest configuration';
      return response;
    }

    const plotlyRaw = await GeneratePlot.getPreprocPlot(
      parent,
      dataFiles,
      report.configuration[typeCard],
      report[ExtraConfigKey],
      true,
      xType,
      yType,
    );
    if (plotlyRaw === null || (!plotlyRaw.success && !plotlyRaw.correct)) {
      let errorMssg = plotlyRaw.lastError || '';
      response.lastError = `Error retrieving the plot from the server. Contact the administrator of the system. ${errorMssg}`;
      return response;
    }

    let areas = [];
    if (
      ConfigName.BaseFeatureCheck in report.configuration[typeCard] &&
      'aoiTimes' in
        report.configuration[typeCard][ConfigName.BaseFeatureCheck]
    )
      areas =
        report.configuration[typeCard][ConfigName.BaseFeatureCheck][
          'aoiTimes'
        ];
    if (concFile !== null && areas.length === 0) {
      areas = concFile.extractedData;
      const currReportConf = report[ExtraConfigKey];
      const timeFactor = TypeAxis.time.getConvertFactor(currReportConf);
      for (let j = 0; j < areas.length; j++) areas[j][1] *= timeFactor;
      const succTitle = 'Areas file found';
      const succText = `An areas file (${concFile.name}) was found in your report. Added to the configuration`;
      parent.$notifTerm.addNotification(
        parent.$notifTerm.typesNotif.SUCCESS,
        succTitle,
        succText,
        zoneNotify,
      );
    }
    const plot =
      typeof plotlyRaw.dataGraph['']['PRE_PROCESS_GRAPH_BOOL'] !==
      'undefined'
        ? plotlyRaw.dataGraph['']['PRE_PROCESS_GRAPH_BOOL']
        : plotlyRaw.dataGraph['']['RAW_GRAPH_BOOL'];

    parent.setPlotAoFConf({
      data: plot.data,
      layout: plot.layout,
      areas: areas,
    });
    response.success = true;
    return response;
  },
};

export default ConfWindowPrep;
